'use client'

import * as React from 'react'
import { CameraIcon } from 'lucide-react'
import Webcam from 'react-webcam'

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Skeleton,
} from '@ui/web/components'
import cn from '@config/tailwind/cn'

import { useCameraDimensions } from './use-camera-dimension'

interface CameraModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onUpload: (file: File) => void
}

export function CameraModal({ open, onOpenChange, onUpload }: CameraModalProps) {
  const containerRef = React.useRef<HTMLDivElement>(null)
  const webcamRef = React.useRef<Webcam>(null)
  const { width, height } = useCameraDimensions(containerRef)

  const [isWebcamReady, setIsWebcamReady] = React.useState(false)
  const [errorMsg, setErrorMsg] = React.useState<string | null>(null)
  const [countdown, setCountdown] = React.useState<number | null>(null)

  const startCountdown = React.useCallback(() => {
    setCountdown(3)

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev === null || prev <= 1) {
          clearInterval(timer)
          return null
        }
        return prev - 1
      })
    }, 1000)

    // Capture after countdown finishes
    setTimeout(() => {
      if (!webcamRef.current) return
      const imageSrc = webcamRef.current.getScreenshot()

      if (imageSrc) {
        // Convert base64 to blob
        const base64Data = imageSrc.split(',')[1]
        if (!base64Data) return

        const byteCharacters = atob(base64Data)
        const byteNumbers = new Array(byteCharacters.length)

        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i)
        }

        const byteArray = new Uint8Array(byteNumbers)
        const blob = new Blob([byteArray], { type: 'image/jpeg' })

        // Create File from blob
        const file = new File([blob], 'webcam-capture.jpg', { type: 'image/jpeg' })

        // Call onUpload with the file
        onUpload(file)

        onOpenChange(false)
      }
    }, 3000)
  }, [webcamRef, onUpload, onOpenChange])

  return (
    <Dialog
      open={open}
      onOpenChange={(open) => {
        onOpenChange(open)

        if (!open) {
          setIsWebcamReady(false)
          setErrorMsg(null)
          setCountdown(null)
        }
      }}
    >
      <DialogContent className='srd:max-w-[1100px] srd:max-h-[90dvh] srd:w-[90vw] srd:h-fit srd:overflow-y-auto'>
        <DialogHeader className='srd:sr-only'>
          <DialogTitle>Kamera</DialogTitle>
          <DialogDescription>Kamera</DialogDescription>
        </DialogHeader>

        <div
          ref={containerRef}
          className='srd:flex srd:flex-col srd:items-center srd:justify-center srd:gap-y-10 srd:pt-4 srd:min-h-[480px]'
        >
          <div className='srd:w-full srd:flex srd:items-center srd:justify-center srd:relative'>
            {!isWebcamReady && <Skeleton style={{ width, height }} />}

            <Webcam
              audio={false}
              ref={webcamRef}
              videoConstraints={{
                width,
                height,
              }}
              screenshotFormat='image/jpeg'
              onUserMedia={() => setIsWebcamReady(true)}
              onUserMediaError={() => {
                setErrorMsg('Kamera tidak dapat diakses')
                setIsWebcamReady(false)
              }}
              className={cn(!isWebcamReady && 'srd:hidden', 'srd:aspect-4/3')}
            />

            {countdown !== null && (
              <div className='srd:absolute srd:inset-0 srd:flex srd:items-center srd:justify-center'>
                <span className='srd:text-8xl srd:font-bold srd:text-white srd:drop-shadow-lg'>{countdown}</span>
              </div>
            )}
          </div>

          <div className='srd:flex srd:flex-col srd:items-center srd:justify-center srd:gap-y-2 srd:w-full'>
            <Button
              type='button'
              className='srd:rounded-full srd:bg-main-blue-20 srd:text-main-blue-100'
              onClick={startCountdown}
              disabled={!isWebcamReady || countdown !== null}
            >
              <CameraIcon className='srd:w-4 srd:h-4 srd:mr-2 srd:text-main-blue-100' />
              {countdown === null ? 'Ambil Foto' : 'Mengambil foto...'}
            </Button>

            {errorMsg && <p className='srd:text-main-red-100 srd:text-sm srd:text-left srd:w-full'>{errorMsg}</p>}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
